import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Row, Col, Card, CardBody, Alert, Button, Spinner,
  FormGroup, Label, Input, Form, Badge
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { config } from '../../config/environment';

const validationSchema = Yup.object({
  enabledProviders: Yup.array()
    .min(1, 'At least one authentication method must be selected')
    .required('Authentication methods are required'),
});

const ConfigureStructure: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [departments, setDepartments] = useState<string[]>([]);
  const [designations, setDesignations] = useState<string[]>([]);
  const [newDepartment, setNewDepartment] = useState('');
  const [newDesignation, setNewDesignation] = useState('');
  const [suggestedDomain, setSuggestedDomain] = useState<string | null>(null);

  const loadLeadData = useCallback(async (onboardingFlowId: string) => {
    try {
      const response = await fetch(config.api.onboardingFlowById(onboardingFlowId), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const onboardingFlow = await response.json();
        if (onboardingFlow.email) {
          const domain = onboardingFlow.email.split('@')[1];
          if (domain) {
            setSuggestedDomain(domain.toLowerCase());
          }
        }
      }
    } catch (error) {
      console.error('Error loading lead data from API:', error);
    }
  }, []);

  const checkOnboardingStatus = useCallback(async (onboardingFlowId: string) => {
    try {
      const response = await fetch(config.api.onboardingFlowById(onboardingFlowId), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const flow = await response.json();
        console.log('📊 Current flow status:', flow.status);
        
        // Only allow access if org is created but structure not configured
        if (flow.status !== 'CONFIGURING_STRUCTURE') {
          if (flow.status === 'COMPLETE') {
            navigate(`/complete?flow=${onboardingFlowId}`);
          } else if (flow.status === 'PAID') {
            navigate(`/create-organization?flow=${onboardingFlowId}`);
          } else {
            navigate(`/getting-started?flow=${onboardingFlowId}`);
          }
          return;
        }
      } else {
        console.error('Failed to fetch onboarding flow status');
        navigate(`/getting-started?flow=${onboardingFlowId}`);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      navigate(`/getting-started?flow=${onboardingFlowId}`);
    }
  }, [navigate]);

  const loadDefaults = useCallback(async (onboardingFlowId: string) => {
    try {
      const response = await fetch(config.api.orgDefaults(onboardingFlowId), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const defaults = await response.json();

        // Set up departments and designations from defaults
        setDepartments(defaults.departments.map((dept: any) => dept.title));
        setDesignations(defaults.designations.map((desig: any) => desig.title));

      } else {
        console.error('Failed to load defaults');
        setError('Failed to load organization defaults');
      }
    } catch (error) {
      console.error('Error loading defaults:', error);
      setError('Error loading organization defaults');
    } finally {
      setLoadingDefaults(false);
    }
  }, []);

  // Check if user has access to this page and load defaults
  useEffect(() => {
    const flowId = searchParams.get('flow') || searchParams.get('session') || searchParams.get('flowId');
    let onboardingFlowId = flowId || localStorage.getItem('onboardingFlowId');

    if (flowId) {
      console.log('🔄 ConfigureStructure - Flow ID from URL:', flowId);
      localStorage.setItem('onboardingFlowId', flowId);
      onboardingFlowId = flowId;
    }

    if (!onboardingFlowId) {
      navigate('/getting-started');
      return;
    }

    checkOnboardingStatus(onboardingFlowId);
    loadDefaults(onboardingFlowId);
    loadLeadData(onboardingFlowId);
  }, [navigate, searchParams, checkOnboardingStatus, loadDefaults, loadLeadData]);

  const formik = useFormik({
    initialValues: {
      enabledProviders: ['google'],
      domainAccessType: 'any' as 'any' | 'restricted',
      allowedDomains: [] as string[],
      newDomain: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      setSuccess('');
      
      try {
        const onboardingFlowId = localStorage.getItem('onboardingFlowId');
        
        if (!onboardingFlowId) {
          setError('No onboarding flow found. Please start over.');
          return;
        }
        
        // Transform departments and designations to expected format
        const departmentsFormatted = departments.map(dept => ({
          title: dept,
          value: dept.toLowerCase().replace(/\s+/g, '-')
        }));
        
        const designationsFormatted = designations.map(desig => ({
          title: desig,
          value: desig.toLowerCase().replace(/\s+/g, '-')
        }));
        
        const configResponse = await fetch(config.api.orgConfigure(onboardingFlowId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            departments: departmentsFormatted,
            designations: designationsFormatted,
            enabledProviders: values.enabledProviders,
            domainAccessType: values.domainAccessType,
            allowedDomains: values.domainAccessType === 'restricted' ? values.allowedDomains : undefined,
          }),
        });

        if (configResponse.ok) {
          await configResponse.json();
          setSuccess(`Organization configured and provisioned successfully! Redirecting to your platform...`);

          // Organization is now fully provisioned, redirect to completion page
          setTimeout(() => {
            const currentFlowId = localStorage.getItem('onboardingFlowId');
            const navigateUrl = currentFlowId ? `/complete?flow=${currentFlowId}` : '/complete';
            navigate(navigateUrl);
          }, 2000);
        } else {
          setError('Failed to configure and provision organization');
        }
      } catch (error) {
        console.error('Error configuring structure:', error);
        setError('Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    },
  });

  const addDepartment = () => {
    if (newDepartment.trim() && !departments.includes(newDepartment.trim())) {
      setDepartments([...departments, newDepartment.trim()]);
      setNewDepartment('');
    }
  };

  const removeDepartment = (index: number) => {
    setDepartments(departments.filter((_, i) => i !== index));
  };

  const addDesignation = () => {
    if (newDesignation.trim() && !designations.includes(newDesignation.trim())) {
      setDesignations([...designations, newDesignation.trim()]);
      setNewDesignation('');
    }
  };

  const removeDesignation = (index: number) => {
    setDesignations(designations.filter((_, i) => i !== index));
  };

  const handleProviderChange = (provider: string) => {
    const currentProviders = formik.values.enabledProviders;
    if (currentProviders.includes(provider)) {
      formik.setFieldValue('enabledProviders', currentProviders.filter(p => p !== provider));
    } else {
      formik.setFieldValue('enabledProviders', [...currentProviders, provider]);
    }
  };

  if (loadingDefaults) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <Spinner color="primary" size="lg" />
          <p className="mt-3 text-muted">Loading organization defaults...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div className="row justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="col-md-8 col-lg-6">
          <div className="text-center mb-5">
            <div className="mb-3">
              <div 
                style={{
                  width: '64px',
                  height: '64px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  fontSize: '1.5rem',
                  color: 'white'
                }}
              >
                ⚙️
              </div>
            </div>
            <h4 className="mb-3">Configure Organization Structure</h4>
            <p className="text-muted">
              Set up your organization's departments, roles, and settings. Once configured, we'll provision your organization with sensible defaults that can be customized later.
            </p>
          </div>

          {error && <Alert color="danger">{error}</Alert>}
          {success && <Alert color="success">{success}</Alert>}

          <Card className="shadow-sm border-0">
            <CardBody className="p-5">
              <Form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col xs="12">
                    <FormGroup>
                      <Label>Departments</Label>
                      <div className="mb-3">
                        {departments.map((dept, index) => (
                          <Badge 
                            key={index} 
                            color="secondary" 
                            className="me-2 mb-2 p-2 d-inline-flex align-items-center"
                          >
                            {dept}
                            <Button
                              close
                              size="sm"
                              className="ms-2"
                              onClick={() => removeDepartment(index)}
                              style={{ fontSize: '0.75rem', padding: '0 0.25rem' }}
                            />
                          </Badge>
                        ))}
                      </div>
                      <div className="input-group">
                        <Input
                          type="text"
                          placeholder="Enter department name"
                          value={newDepartment}
                          onChange={(e) => setNewDepartment(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDepartment())}
                        />
                        <Button 
                          type="button" 
                          color="outline-primary" 
                          onClick={addDepartment}
                          disabled={!newDepartment.trim()}
                        >
                          Add
                        </Button>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col xs="12">
                    <FormGroup>
                      <Label>Designations</Label>
                      <div className="mb-3">
                        {designations.map((desig, index) => (
                          <Badge 
                            key={index} 
                            color="secondary" 
                            className="me-2 mb-2 p-2 d-inline-flex align-items-center"
                          >
                            {desig}
                            <Button
                              close
                              size="sm"
                              className="ms-2"
                              onClick={() => removeDesignation(index)}
                              style={{ fontSize: '0.75rem', padding: '0 0.25rem' }}
                            />
                          </Badge>
                        ))}
                      </div>
                      <div className="input-group">
                        <Input
                          type="text"
                          placeholder="Enter designation"
                          value={newDesignation}
                          onChange={(e) => setNewDesignation(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDesignation())}
                        />
                        <Button 
                          type="button" 
                          color="outline-primary" 
                          onClick={addDesignation}
                          disabled={!newDesignation.trim()}
                        >
                          Add
                        </Button>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col xs="12">
                    <FormGroup>
                      <Label>Authentication Methods *</Label>
                      <div className="mt-3">
                        <FormGroup check className="mb-3">
                          <Input
                            type="checkbox"
                            id="google"
                            checked={formik.values.enabledProviders.includes('google')}
                            onChange={() => handleProviderChange('google')}
                          />
                          <Label check for="google" className="d-flex align-items-center">
                            <strong>Google OAuth</strong>
                            <span className="text-muted ms-2">- Sign in with Google accounts</span>
                          </Label>
                        </FormGroup>
                        
                        <FormGroup check className="mb-3">
                          <Input
                            type="checkbox"
                            id="microsoft"
                            checked={formik.values.enabledProviders.includes('microsoft')}
                            onChange={() => handleProviderChange('microsoft')}
                          />
                          <Label check for="microsoft" className="d-flex align-items-center">
                            <strong>Microsoft OAuth</strong>
                            <span className="text-muted ms-2">- Sign in with Microsoft/Azure AD accounts</span>
                          </Label>
                        </FormGroup>
                        
                        <FormGroup check className="mb-3">
                          <Input
                            type="checkbox"
                            id="email-password"
                            checked={formik.values.enabledProviders.includes('email-password')}
                            onChange={() => handleProviderChange('email-password')}
                          />
                          <Label check for="email-password" className="d-flex align-items-center">
                            <strong>Email & Password</strong>
                            <span className="text-muted ms-2">- Traditional email/password authentication</span>
                          </Label>
                        </FormGroup>
                      </div>
                      {formik.touched.enabledProviders && formik.errors.enabledProviders && (
                        <div className="text-danger small">{formik.errors.enabledProviders}</div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col xs="12">
                    <FormGroup>
                      <Label>Domain Access Control</Label>
                      <div className="mt-3">
                        <FormGroup>
                          <Input
                            type="radio"
                            name="domainAccessType"
                            id="domainAccessAny"
                            value="any"
                            checked={formik.values.domainAccessType === 'any'}
                            onChange={formik.handleChange}
                          />
                          <Label check for="domainAccessAny" className="ms-2">
                            Allow any email domain
                          </Label>
                        </FormGroup>

                        <FormGroup>
                          <Input
                            type="radio"
                            name="domainAccessType"
                            id="domainAccessRestricted"
                            value="restricted"
                            checked={formik.values.domainAccessType === 'restricted'}
                            onChange={formik.handleChange}
                          />
                          <Label check for="domainAccessRestricted" className="ms-2">
                            Only allow specific domains
                          </Label>
                        </FormGroup>

                        {formik.values.domainAccessType === 'restricted' && (
                          <div className="mt-3">
                            <Label>Allowed Domains</Label>
                            {suggestedDomain && !formik.values.allowedDomains.includes(suggestedDomain) && (
                              <div className="mt-2 mb-3">
                                <small className="text-muted">Suggested based on your email:</small>
                                <div className="d-flex align-items-center mt-1">
                                  <Badge color="info" className="me-2">{suggestedDomain}</Badge>
                                  <Button
                                    type="button"
                                    color="outline-success"
                                    size="sm"
                                    onClick={() => {
                                      formik.setFieldValue('allowedDomains', [...formik.values.allowedDomains, suggestedDomain]);
                                    }}
                                  >
                                    Add this domain
                                  </Button>
                                </div>
                              </div>
                            )}
                            <div className="mt-2">
                              {formik.values.allowedDomains.map((domain, index) => (
                                <div key={index} className="d-flex align-items-center mb-2">
                                  <span className="badge bg-primary me-2">{domain}</span>
                                  <Button
                                    type="button"
                                    color="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                      const newDomains = formik.values.allowedDomains.filter((_, i) => i !== index);
                                      formik.setFieldValue('allowedDomains', newDomains);
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              ))}

                              <div className="d-flex gap-2">
                                <Input
                                  type="text"
                                  placeholder="Enter domain (e.g., example.com)"
                                  value={formik.values.newDomain || ''}
                                  onChange={(e) => formik.setFieldValue('newDomain', e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      const domain = (e.target as HTMLInputElement).value.trim().toLowerCase();
                                      if (domain && !formik.values.allowedDomains.includes(domain)) {
                                        formik.setFieldValue('allowedDomains', [...formik.values.allowedDomains, domain]);
                                        formik.setFieldValue('newDomain', '');
                                      }
                                    }
                                  }}
                                />
                                <Button
                                  type="button"
                                  color="outline-primary"
                                  onClick={() => {
                                    const domain = formik.values.newDomain?.trim().toLowerCase();
                                    if (domain && !formik.values.allowedDomains.includes(domain)) {
                                      formik.setFieldValue('allowedDomains', [...formik.values.allowedDomains, domain]);
                                      formik.setFieldValue('newDomain', '');
                                    }
                                  }}
                                >
                                  Add
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end pt-3">
                  <Button 
                    type="submit" 
                    color="primary" 
                    size="lg"
                    disabled={loading || departments.length === 0 || designations.length === 0 || formik.values.enabledProviders.length === 0}
                  >
                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                    Complete & Provision Organization
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ConfigureStructure;